import React, { useEffect, useState } from 'react';
import { useToast } from '../ui';
import { invoiceAPI, customerAPI } from '../../services/api';
import useCompanyProfile from '../../hooks/useCompanyProfile';
import SendComposerModal, { defaultInvoiceSubject } from '../shared/SendComposerModal';
import { seedRecipientsFromContacts } from '../shared/ReviewAndSendShell';
import { resendPrefill, acceptedDeliveries, failedDeliveries, invoiceSentToast, invoicePartialToast, resendFailedToast } from './invoiceLifecycle';

/**
 * SendAgainComposer (IDI-580) — Send again on a Sent, Past due or Paid
 * invoice: IDI-485's composer pre-filled from the last send (D1; IDI-597:
 * then the customer's other contacts, unticked), or with
 * 485's default subject and the customer's contacts when there is no stored
 * lastSend (D4). Sends through 485's path; the status never changes (D2).
 *
 * Mount it only while open. `onSent` runs after any send that reached the
 * server (accepted or every recipient refused) so the caller can reload.
 */
export default function SendAgainComposer({ invoice, onClose, onSent }) {
  const toast = useToast();
  const { companyName } = useCompanyProfile();
  const [prefill, setPrefill] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [sending, setSending] = useState(false);
  const number = invoice?.invoiceNumber || invoice?.invoice_number || '';
  const defaultSubject = defaultInvoiceSubject(number || null, companyName);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // A list row may be a summary; the stored lastSend and deliveries are
      // read from the full record.
      let inv = invoice;
      try {
        const res = await invoiceAPI.getInvoice(invoice.id);
        if (res && typeof res === 'object') inv = { ...invoice, ...(res.data && res.data.id ? res.data : res) };
      } catch { /* fall back to what we have */ }
      // IDI-597 (D1): the customer's contacts are always loaded, seeded as
      // the first send seeds them; with a lastSend they follow its
      // recipients, unticked.
      let contacts = [];
      const cid = inv.customerId || inv.customer_id;
      const fallback = { email: inv.customerEmail || inv.customer_email || '', name: inv.customerName || inv.customer_name || '' };
      try {
        const cust = cid ? await customerAPI.getCustomer(cid) : null;
        const c = (cust && (cust.data || cust)) || {};
        contacts = seedRecipientsFromContacts(Array.isArray(c.contacts) ? c.contacts : [], {
          email: c.email || fallback.email, name: c.name || fallback.name,
        });
      } catch {
        contacts = seedRecipientsFromContacts([], fallback);
      }
      let p = resendPrefill(inv, contacts, defaultSubject);
      // A lastSend with no recipient rows ticks every contact, as without one.
      if (!p.recipients.some((r) => r.checked)) {
        p = { ...p, recipients: resendPrefill({ ...inv, lastSend: null }, contacts, defaultSubject).recipients };
      }
      if (cancelled) return;
      setPrefill(p);
      setRecipients(p.recipients);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);

  const send = async ({ subject, message, cc, recipients: to }) => {
    setSending(true);
    try {
      const res = await invoiceAPI.sendInvoice(invoice.id, {
        recipients: to,
        subject,
        message,
        ...(cc && cc.length ? { cc } : {}),
      });
      const sent = (res && (res.data && res.data.id ? res.data : res)) || {};
      const num = sent.invoiceNumber || sent.invoice_number || number;
      if (acceptedDeliveries(sent).length === 0) toast.error(resendFailedToast(num, sent));
      else if (failedDeliveries(sent).length > 0) toast.warning(invoicePartialToast(num, sent));
      else toast.success(invoiceSentToast(num, sent));
      setSending(false);
      onClose?.();
      await Promise.resolve(onSent?.()).catch(() => {});
    } catch (err) {
      setSending(false);
      toast.error(err?.response?.data?.message || err?.friendlyMessage || `Failed to send ${number || 'the invoice'}.`);
    }
  };

  if (!prefill) return null;
  return (
    <SendComposerModal
      open
      title="Send Again"
      onClose={onClose}
      onConfirm={send}
      defaultSubject={defaultSubject}
      initial={{ subject: prefill.subject, message: prefill.message, cc: prefill.cc }}
      recipients={recipients}
      onRecipientsChange={setRecipients}
      sending={sending}
    />
  );
}
